/**
 * Initialize "Show More" behavior, for archive filter forms.
 *
 * @param {HTMLFormElement} form The archive filters form element.
 */
export function initShowMoreFilters( form ) {
	if ( ! form ) {
		return;
	}

	const showMoreButton = form.querySelector( '.edawp-archive-form__show-more' );

	if ( ! showMoreButton ) {
		return;
	}

	if ( showMoreButton.dataset.edawpShowMoreInitialized === 'true' ) {
		return;
	}

	showMoreButton.dataset.edawpShowMoreInitialized = 'true';

	const filtersWrapper = form.querySelector( '.edawp-archive-form__filters' );

	if ( ! filtersWrapper ) {
		return;
	}

	const hiddenFilters = Array.from(
		filtersWrapper.querySelectorAll( '[data-hidden="true"]' )
	);

	if ( hiddenFilters.length === 0 ) {
		return;
	}

	const hiddenClass = 'edawp-archive-form__label--hidden';
	const showMoreText = ( showMoreButton.dataset.showMoreText || showMoreButton.textContent || '' ).trim();
	const showLessText = ( showMoreButton.dataset.showLessText || 'Show Less' ).trim();
	let isExpanded = false;

	function focusFirstRevealedInput() {
		const firstHiddenFilter = hiddenFilters[ 0 ];

		if ( ! firstHiddenFilter ) {
			return;
		}

		const focusable = firstHiddenFilter.querySelector(
			'input:not([disabled]):not([type="hidden"]), select, textarea, button, [tabindex]:not([tabindex="-1"])'
		);

		if ( focusable && typeof focusable.focus === 'function' ) {
			const scheduleFocus = typeof requestAnimationFrame === 'function'
				? requestAnimationFrame
				: function( callback ) {
					setTimeout( callback, 0 );
				};
			// Defer to the next frame so the element is focusable when focus runs.
			scheduleFocus( function() {
				focusable.focus();
			} );
		}
	}

	function setButtonText( text ) {
		if ( text.length ) {
			showMoreButton.textContent = text;
		}
	}

	function setAriaExpanded( expanded ) {
		if ( showMoreButton.hasAttribute( 'aria-expanded' ) ) {
			showMoreButton.setAttribute( 'aria-expanded', expanded ? 'true' : 'false' );
		}
	}

	function showHiddenFilters() {
		hiddenFilters.forEach( function( filter ) {
			filter.classList.remove( hiddenClass );
			filter.removeAttribute( 'data-hidden' );
		} );

		isExpanded = true;
		showMoreButton.dataset.edawpExpanded = 'true';
		setButtonText( showLessText );
		setAriaExpanded( true );
		focusFirstRevealedInput();

		form.dispatchEvent(
			new CustomEvent(
				'edawpArchiveFiltersExpanded',
				{
					bubbles: true,
				}
			)
		);
	}

	function hideHiddenFilters() {
		hiddenFilters.forEach( function( filter ) {
			filter.classList.add( hiddenClass );
			filter.setAttribute( 'data-hidden', 'true' );
		} );

		isExpanded = false;
		showMoreButton.dataset.edawpExpanded = 'false';
		setButtonText( showMoreText );
		setAriaExpanded( false );
	}

	setButtonText( showMoreText );
	setAriaExpanded( false );
	showMoreButton.dataset.edawpExpanded = 'false';

	// Keyboard accessibility for <a role="button">
	showMoreButton.addEventListener( 'keydown', function( event ) {
		if ( event.key === 'Enter' || event.key === ' ' ) {
			event.preventDefault();
			showMoreButton.click();
		}
	} );

	showMoreButton.addEventListener( 'click', function( event ) {
		event.preventDefault();

		if ( isExpanded ) {
			hideHiddenFilters();
			return;
		}

		showHiddenFilters();
	} );
}

export default initShowMoreFilters;
